import type { PrivacyConsent } from "@/types/share"
import { useState, useEffect, useCallback, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Loader2, History, Download, Calendar, RotateCcw, Trash2, LayoutTemplate, Globe, Share2, Link2, Unlink2, Copy, Check, ExternalLink } from "lucide-react"
import { VideoPlayer } from "@/components/common/VideoPlayer"
import { AiGeneratedNotice } from "@/components/common/AiGeneratedNotice"
import { ImageWithFallback } from "@/components/common/ImageWithFallback"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { toast } from "sonner"
import { useInfiniteHistory, useDeleteHistory, useGenerateVideo, type HistoryItem } from "@/hooks/useHistory"
import { useMySharedVideos, useShareVideo, useUnshareVideo } from "@/hooks/useShare"
import { useAuthMe } from "@/hooks/useAuthMe"
import { ShareDialog } from "@/features/share/components/ShareDialog"
import { ShareSuccessDialog } from "@/features/share/components/ShareSuccessDialog"
import { useTemplate } from "@/hooks/useTemplates"
import { useGeneratorStore } from "@/stores/generatorStore"
import { downloadVideo, DownloadError } from "@/lib/utils"
import { normalizeVideoDuration, normalizeVideoResolution } from "@/lib/video-settings"
import { reportGenerateError } from "@/features/generator/lib/report-generate-error"
import { currentLocale } from "@/lib/i18n-locale"
import { localizeName } from "@/lib/i18n-content"
import type { FrameImage, ApplyableSettings } from "@/types/generation"
import { TemplatePreviewModal } from "@/features/templates/components/TemplatePreviewModal"
import { extractSettingsFromTemplate } from "@/features/templates/lib/extract-settings-from-template"
import { restoreTemplateParameters } from "@/features/templates/lib/restore-template-parameters"
import { QueuePlaceholderCard } from "./QueuePlaceholderCard"
import { ExpandableText } from "@/components/common/ExpandableText"

function isPortraitRatio(ratio: string): boolean {
  const [w, h] = ratio.split(':').map(Number)
  return h > w
}

function getPrimaryVideoThumbnail(
  generatedVideo: { thumbnailUrls?: string[]; thumbnailUrl?: string | null } | null | undefined
): string | null {
  if (!generatedVideo) return null
  if (generatedVideo.thumbnailUrls && generatedVideo.thumbnailUrls.length > 0) {
    return generatedVideo.thumbnailUrls[0] || null
  }
  return generatedVideo.thumbnailUrl ?? null
}

/**
 * HistoryItem에서 ApplyableSettings로 변환
 */
function extractSettingsFromHistory(item: HistoryItem): ApplyableSettings {
  const frameImages: FrameImage[] = (item.frameImages ?? []).map((fi, idx) => ({
    id: `reuse-${item.id}-${idx}`,
    role: fi.role,
    s3Key: fi.s3Key,
    thumbnailS3Key: fi.thumbnailS3Key,
    url: item.frameImageUrls?.[idx] || '',
    thumbnailUrl: item.frameImageUrls?.[idx] || '',
  }))

  return {
    prompt: item.prompt,
    ratio: item.ratio || undefined,
    resolution: item.resolution || undefined,
    duration: item.duration || undefined,
    frameImages: frameImages.length > 0 ? frameImages : undefined,
    // 이름은 사이드바가 현재 언어로 표시한다
    template: item.template ? {
      id: item.template.id,
      name: item.template.name,
      description: item.template.description ?? null,
      nameI18n: item.template.nameI18n,
      descriptionI18n: item.template.descriptionI18n,
      thumbnailUrl: item.template.thumbnailUrl,
    } : undefined,
  }
}

/** The `POST /generate` body that sends a failed or cancelled generation again, as it was asked for
 * (the template's prompt suffix is appended again by the server). */
function buildRetryRequest(item: HistoryItem): Record<string, unknown> {
  return {
    prompt: item.prompt,
    ratio: item.ratio,
    resolution: normalizeVideoResolution(item.resolution),
    duration: normalizeVideoDuration(item.duration),
    frameImages: item.frameImages?.length
      ? item.frameImages.map((fi) => ({
          role: fi.role,
          s3Key: fi.s3Key,
          thumbnailS3Key: fi.thumbnailS3Key,
          filename: fi.filename,
        }))
      : undefined,
    templateId: item.template?.id,
  }
}

export function HistoryTab() {
  const { t } = useTranslation()
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteHistory(10)
  const deleteMutation = useDeleteHistory()
  const retryMutation = useGenerateVideo()
  const [retryingId, setRetryingId] = useState<string | null>(null)
  const { data: sharedVideosMap } = useMySharedVideos()
  const shareMutation = useShareVideo()
  const unshareMutation = useUnshareVideo()
  const { data: authMe } = useAuthMe()
  const communityHidden = authMe?.menuHidden.community ?? false
  const applySettings = useGeneratorStore((state) => state.applySettings)
  const observerTarget = useRef<HTMLDivElement>(null)

  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  // 삭제 확인 다이얼로그 상태
  const [deleteTarget, setDeleteTarget] = useState<HistoryItem | null>(null)
  // 템플릿 상세 모달 상태
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null)
  // 공유 다이얼로그 상태
  const [shareTarget, setShareTarget] = useState<{
    generationId: string
    videoUrl: string
    thumbnailUrl: string | null
    ratio: string
  } | null>(null)
  // 공유 성공 다이얼로그 상태
  const [shareSuccess, setShareSuccess] = useState<{
    shareId: string
    videoUrl: string
    thumbnailUrl: string | null
    ratio: string
  } | null>(null)
  // 공유 정보 다이얼로그 상태
  const [shareInfoTarget, setShareInfoTarget] = useState<{
    shareId: string
    videoUrl: string
    thumbnailUrl: string | null
  } | null>(null)
  const [shareInfoCopied, setShareInfoCopied] = useState(false)
  // 공유 취소 확인 다이얼로그 상태
  const [unshareTarget, setUnshareTarget] = useState<{
    shareId: string
    videoUrl: string
  } | null>(null)

  // 템플릿 상세 조회 (모달용)
  const { data: previewTemplate } = useTemplate(previewTemplateId)

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { threshold: 0.1 }
    )

    if (observerTarget.current) {
      observer.observe(observerTarget.current)
    }

    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString(currentLocale(), {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const handleDownload = useCallback(async (videoUrl: string, videoS3Key: string) => {
    const videoId = videoS3Key.split('/').pop()?.replace(/\.\w+$/, '') || 'video'
    setDownloadingId(videoId)

    try {
      await downloadVideo(videoUrl, `wrks-${videoId}.mp4`)
      toast.success(t('history.downloadSuccessTitle'), {
        description: t('history.downloadSuccessDescription'),
      })
    } catch (error) {
      if (error instanceof DownloadError && error.status === 404) {
        toast.error(t('history.downloadNotFound'))
      } else if (error instanceof DownloadError && error.status >= 500) {
        toast.error(t('history.downloadServerError'), {
          description: t('common.tryAgainLater'),
        })
      } else {
        toast.error(t('history.downloadFailed'))
      }
    } finally {
      setDownloadingId(null)
    }
  }, [t])

  const handleRetry = async (item: HistoryItem) => {
    if (retryingId) return
    setRetryingId(item.id)
    try {
      await retryMutation.mutateAsync(buildRetryRequest(item))
      toast.success(t('queue.retryStarted'))
    } catch (error: unknown) {
      reportGenerateError(error, t)
    } finally {
      setRetryingId(null)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return

    try {
      await deleteMutation.mutateAsync(deleteTarget.id)
      toast.success(t('history.deleteSuccessTitle'), {
        description: t('history.deleteSuccessDescription'),
      })
      setDeleteTarget(null)
    } catch {
      toast.error(t('history.deleteFailed'))
    }
  }

  const handleShareConfirm = async (consent: PrivacyConsent) => {
    if (!shareTarget) return

    const result = await shareMutation.mutateAsync({
      ...consent,
      generationId: shareTarget.generationId,
    })
    setShareTarget(null)
    setShareSuccess({
      shareId: result.id,
      videoUrl: shareTarget.videoUrl,
      thumbnailUrl: shareTarget.thumbnailUrl,
      ratio: shareTarget.ratio,
    })
  }

  const handleUnshareConfirm = async () => {
    if (!unshareTarget) return

    try {
      await unshareMutation.mutateAsync(unshareTarget.shareId)
      toast.success(t('history.unshareSuccess'))
      setUnshareTarget(null)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('history.unshareFailed'))
    }
  }

  const isVideoShared = (s3Key: string): boolean => {
    return sharedVideosMap?.has(s3Key) ?? false
  }

  const getShareId = (s3Key: string): string | undefined => {
    return sharedVideosMap?.get(s3Key)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-500">
        <p>{t('history.loadFailed')}</p>
        <p className="text-sm text-slate-400 mt-1">{t('common.tryAgainLater')}</p>
      </div>
    )
  }

  // Flatten all pages into a single array (hide cancelled items from user view)
  const allItems = (data?.pages.flatMap((page) => page.items) || [])
    .filter((item) => item.status !== 'cancelled')

  if (!allItems.length) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
          <History className="w-10 h-10 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          {t('history.emptyTitle')}
        </h3>
        <p className="text-slate-500 text-sm">
          {t('history.emptyDescriptionLine1')}
          <br />
          {t('history.emptyDescriptionLine2')}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* 히스토리 (1 item = 1 generation request) */}
      {allItems.map((item) => {
        // Processing/failed/cancelled items use QueuePlaceholderCard
        if (item.status === 'processing' || item.status === 'failed' || item.status === 'cancelled') {
          return (
            <QueuePlaceholderCard
              key={item.id}
              item={item}
              onRetry={() => handleRetry(item)}
              retrying={retryingId === item.id}
              onDelete={() => setDeleteTarget(item)}
              onReuse={async () => {
                const settings = await restoreTemplateParameters(extractSettingsFromHistory(item))
                applySettings(settings)
                toast.success(t('history.reuseSuccessTitle'), {
                  description: t('history.reuseSuccessDescription'),
                })
              }}
            />
          )
        }

        // Completed items — full video card
        const videoId = item.generatedVideo?.s3Key.split('/').pop()?.replace(/\.\w+$/, '') || ''
        const shared = item.generatedVideo ? isVideoShared(item.generatedVideo.s3Key) : false
        const primaryThumbnailUrl = getPrimaryVideoThumbnail(item.generatedVideo)

        return (
          <div
            key={item.id}
            className="bg-white rounded-xl border border-slate-200 overflow-hidden"
          >
            {/* 생성 정보 헤더 */}
            <div className="p-4 border-b border-slate-100">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                {/* 프롬프트 + 태그 */}
                <div className="flex-1 min-w-0">
                  <ExpandableText
                    text={item.prompt}
                    className="text-slate-800 text-sm"
                    lineClamp={2}
                  />
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <span className="inline-flex items-center text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md">
                      {item.ratio}
                    </span>
                    {item.resolution && (
                      <span className="inline-flex items-center text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md">
                        {item.resolution}
                      </span>
                    )}
                    {item.duration && (
                      <span className="inline-flex items-center text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md">
                        {t('common.seconds', { count: item.duration })}
                      </span>
                    )}
                  </div>
                </div>
                {/* 액션 + 메타 */}
                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={async () => {
                        const settings = await restoreTemplateParameters(extractSettingsFromHistory(item))
                        applySettings(settings)
                        toast.success(t('history.reuseSuccessTitle'), {
                          description: t('history.reuseSuccessDescription'),
                        })
                      }}
                      className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      <span>{t('history.reuse')}</span>
                    </button>
                    <span className="text-slate-200">|</span>
                    <div className="flex items-center gap-1 text-xs text-slate-400">
                      <Calendar className="w-3.5 h-3.5" />
                      {formatDate(item.createdAt)}
                    </div>
                    <span className="text-slate-200">|</span>
                    <button
                      onClick={() => setDeleteTarget(item)}
                      disabled={deleteMutation.isPending}
                      className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-500 transition-colors disabled:opacity-50"
                      title={t('common.delete')}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {/* 템플릿 + 프레임 이미지 */}
                  {(item.template || (item.frameImageUrls && item.frameImageUrls.length > 0)) && (
                    <div className="flex items-center gap-3">
                      {/* 템플릿 썸네일 */}
                      {item.template && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-slate-500">{t('history.templateLabel')}</span>
                          <button
                            onClick={() => setPreviewTemplateId(item.template!.id)}
                            className="w-10 h-10 rounded border border-primary/30 bg-primary/5 hover:border-primary hover:bg-primary/10 transition-colors overflow-hidden flex items-center justify-center cursor-pointer"
                            title={localizeName(item.template)}
                          >
                            {item.template.thumbnailUrl ? (
                              <ImageWithFallback
                                src={item.template.thumbnailUrl}
                                alt={localizeName(item.template)}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <LayoutTemplate className="w-5 h-5 text-primary/60" />
                            )}
                          </button>
                        </div>
                      )}
                      {/* 프레임 이미지 */}
                      {item.frameImageUrls && item.frameImageUrls.length > 0 &&
                        item.frameImageUrls.map((url, idx) => {
                          const role = item.frameImages?.[idx]?.role
                          const label = role === 'first_frame' ? t('generator.firstFrame') : role === 'last_frame' ? t('generator.lastFrame') : t('history.frameLabel', { index: idx + 1 })
                          return (
                            <div key={idx} className="flex items-center gap-1.5">
                              <span className="text-xs text-slate-500">{label}</span>
                              <div
                                className="w-10 h-10 rounded border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center"
                                title={label}
                              >
                                <ImageWithFallback
                                  src={url}
                                  alt={label}
                                  className="max-w-full max-h-full object-contain pointer-events-none"
                                  fallbackClassName="w-full h-full"
                                />
                              </div>
                            </div>
                          )
                        })
                      }
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 비디오 영역 */}
            {item.generatedVideo && (
              <div className="p-4">
                <div className={`${isPortraitRatio(item.ratio) ? 'max-w-xs' : 'max-w-lg'} group relative`}>
                  {/* 공유 뱃지 */}
                  {shared && (
                    <div className="absolute top-2 left-2 z-10">
                      <button
                        onClick={() => {
                          const shareId = getShareId(item.generatedVideo!.s3Key)
                          if (shareId) {
                            setShareInfoTarget({
                              shareId,
                              videoUrl: item.generatedVideo!.videoUrl,
                              thumbnailUrl: primaryThumbnailUrl,
                            })
                          }
                        }}
                        className="flex items-center gap-1 px-2 py-1 bg-primary/90 lg:hover:bg-primary text-white text-xs rounded-full transition-colors pointer-events-none lg:pointer-events-auto"
                        title={t('history.viewShareInfo')}
                      >
                        <Globe className="w-3 h-3" />
                        {t('history.sharedBadge')}
                      </button>
                    </div>
                  )}
                  <VideoPlayer
                    src={item.generatedVideo.videoUrl}
                    poster={primaryThumbnailUrl ?? undefined}
                    controls
                    preload="none"
                    deferSrcUntilPlay
                    className="w-full rounded-lg"
                    style={{ aspectRatio: item.ratio.replace(':', '/') }}
                  />
                  <AiGeneratedNotice className="mt-2" />
                  {/* Hover actions - 데스크톱 전용 */}
                  <div className="hidden lg:flex absolute top-2 right-2 gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => handleDownload(item.generatedVideo!.videoUrl, item.generatedVideo!.s3Key)}
                            disabled={downloadingId === videoId}
                            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/90 hover:bg-white text-slate-700 transition-colors shadow-md disabled:opacity-50"
                          >
                            {downloadingId === videoId ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">{t('history.download')}</TooltipContent>
                      </Tooltip>
                      {!shared && !communityHidden && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => {
                                setShareTarget({
                                  generationId: item.id,
                                  videoUrl: item.generatedVideo!.videoUrl,
                                  thumbnailUrl: primaryThumbnailUrl,
                                  ratio: item.ratio,
                                })
                              }}
                              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/90 hover:bg-white text-slate-700 transition-colors shadow-md"
                            >
                              <Share2 className="w-4 h-4" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top">{t('history.share')}</TooltipContent>
                        </Tooltip>
                      )}
                    </TooltipProvider>
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      })}

      {/* Infinite scroll trigger & loading indicator */}
      {hasNextPage && (
        <div
          ref={observerTarget}
          className="flex items-center justify-center py-8"
        >
          {isFetchingNextPage && (
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          )}
        </div>
      )}

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{t('history.deleteDialogTitle')}</DialogTitle>
            <DialogDescription className="mt-2">
              {t('history.deleteDialogDescription1')}
              <br />
              {t('history.deleteDialogDescription2')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('history.deleting')}
                </>
              ) : (
                t('common.delete')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 템플릿 프리뷰 모달 */}
      <TemplatePreviewModal
        template={previewTemplate ?? null}
        isOpen={previewTemplateId !== null}
        onClose={() => setPreviewTemplateId(null)}
        onUse={previewTemplate ? () => {
          const settings = extractSettingsFromTemplate(previewTemplate)
          applySettings(settings)
          toast.success(t('history.templateAppliedTitle'), {
            description: t('history.templateAppliedDescription', { name: localizeName(previewTemplate) }),
          })
        } : undefined}
      />

      {/* 공유 확인 다이얼로그 */}
      <ShareDialog
        open={!!shareTarget}
        onOpenChange={(open) => !open && setShareTarget(null)}
        onConfirm={handleShareConfirm}
        generationId={shareTarget?.generationId ?? ""}
        isLoading={shareMutation.isPending}
        videoUrl={shareTarget?.videoUrl ?? ""}
        thumbnailUrl={shareTarget?.thumbnailUrl ?? null}
        ratio={shareTarget?.ratio}
      />

      {/* 공유 성공 다이얼로그 */}
      <ShareSuccessDialog
        open={!!shareSuccess}
        onOpenChange={(open) => !open && setShareSuccess(null)}
        shareId={shareSuccess?.shareId ?? ""}
        videoUrl={shareSuccess?.videoUrl ?? ""}
        thumbnailUrl={shareSuccess?.thumbnailUrl ?? null}
        ratio={shareSuccess?.ratio}
      />

      {/* 공유 정보 다이얼로그 */}
      <Dialog open={!!shareInfoTarget} onOpenChange={(open) => !open && setShareInfoTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-primary" />
              {t('history.shareLinkTitle')}
            </DialogTitle>
            <DialogDescription className="mt-2">
              {t('history.shareLinkDescription')}
            </DialogDescription>
          </DialogHeader>
          {shareInfoTarget && (
            <>
              {/* 비디오 미리보기 */}
              <div className="flex justify-center py-4">
                <VideoPlayer
                  src={shareInfoTarget.videoUrl}
                  poster={shareInfoTarget.thumbnailUrl || undefined}
                  controls
                  className="max-w-full max-h-32 rounded-lg border border-slate-200"
                  muted
                  preload="none"
                />
              </div>
              {/* URL 복사 영역 */}
              {(() => {
                const basePath = (import.meta.env.VITE_BASE_PATH || '').replace(/\/$/, '')
                const shareUrl = `${window.location.origin}${basePath}/share/${shareInfoTarget.shareId}`
                return (
                  <div className="flex items-center gap-2">
                    <Input
                      value={shareUrl}
                      readOnly
                      className="flex-1 text-sm bg-slate-50"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(shareUrl)
                          setShareInfoCopied(true)
                          toast.success(t('history.urlCopied'))
                          setTimeout(() => setShareInfoCopied(false), 2000)
                        } catch {
                          toast.error(t('history.copyFailed'))
                        }
                      }}
                    >
                      {shareInfoCopied ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      onClick={() => window.open(shareUrl, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                )
              })()}
            </>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="default"
              onClick={() => {
                if (shareInfoTarget) {
                  setUnshareTarget({
                    shareId: shareInfoTarget.shareId,
                    videoUrl: shareInfoTarget.videoUrl,
                  })
                  setShareInfoTarget(null)
                }
              }}
            >
              <Unlink2 className="w-4 h-4 mr-1.5" />
              {t('history.unshare')}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShareInfoTarget(null)}
            >
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 공유 취소 확인 다이얼로그 */}
      <Dialog open={!!unshareTarget} onOpenChange={(open) => !open && setUnshareTarget(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{t('history.unshare')}</DialogTitle>
            <DialogDescription className="mt-2">
              {t('history.unshareDialogDescription1')}
              <br />
              {t('history.unshareDialogDescription2')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setUnshareTarget(null)}
              disabled={unshareMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleUnshareConfirm}
              disabled={unshareMutation.isPending}
            >
              {unshareMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('history.processing')}
                </>
              ) : (
                t('common.confirm')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
